/**
 * Environment Configuration for BotForge Widget
 *
 * Build-time environment configuration using Vite environment variables.
 * Values are injected during the build process from .env files.
 *
 * Security: Development/staging URLs are NOT included in production builds.
 */

export type Environment = 'development' | 'staging' | 'production';

export interface EnvironmentConfig {
  environment: Environment;
  isDevelopment: boolean;
  isProduction: boolean;
  isStaging: boolean;

  // Firebase Configuration
  useEmulators: boolean;
  emulatorHosts: {
    auth: string;
    functions: number;
    database: number;
  };

  // API Configuration
  apiBaseUrl: string;
  functionsRegion: string;

  // Widget Configuration
  widgetBaseUrl: string;
  widgetOrigin: string;

  // Feature Flags
  enableLogging: boolean;
  enableAnalytics: boolean;
  enableErrorTracking: boolean;

  // Session Configuration
  sessionCacheKey: string;
  sessionExpirationMs: number;
}

/**
 * Get current origin for postMessage validation
 * This is the only runtime detection we keep
 */
function getWidgetOrigin(): string {
  return window.location.origin;
}

/**
 * Global environment configuration instance
 * Values are replaced at BUILD TIME - no runtime detection
 *
 * The replace plugin will replace import.meta.env.VITE_* with actual values
 */
export const ENV: EnvironmentConfig = (() => {
  const environment = (import.meta.env.VITE_ENVIRONMENT || 'production') as Environment;
  const isDevelopment = environment === 'development';
  const isStaging = environment === 'staging';
  const isProduction = environment === 'production';

  return {
    environment,
    isDevelopment,
    isProduction,
    isStaging,

    // Firebase Configuration - injected at build time
    useEmulators: import.meta.env.VITE_USE_EMULATORS === 'true',
    emulatorHosts: {
      auth: import.meta.env.VITE_EMULATOR_AUTH || '',
      functions: parseInt(import.meta.env.VITE_EMULATOR_FUNCTIONS || '0', 10),
      database: parseInt(import.meta.env.VITE_EMULATOR_DATABASE || '0', 10),
    },

    // API Configuration - injected at build time
    apiBaseUrl: import.meta.env.VITE_API_BASE_URL || '',
    functionsRegion: import.meta.env.VITE_FUNCTIONS_REGION || 'europe-west1',

    // Widget Configuration
    widgetBaseUrl: import.meta.env.VITE_WIDGET_BASE_URL || '',
    widgetOrigin: getWidgetOrigin(), // Runtime detection (safe)

    // Feature Flags - injected at build time
    enableLogging: import.meta.env.VITE_ENABLE_LOGGING === 'true',
    enableAnalytics: import.meta.env.VITE_ENABLE_ANALYTICS === 'true',
    enableErrorTracking: import.meta.env.VITE_ENABLE_ERROR_TRACKING === 'true',

    // Session Configuration
    sessionCacheKey: 'BotForgeSession',
    sessionExpirationMs: parseInt(import.meta.env.VITE_SESSION_EXPIRATION_MS || '86400000', 10),
  };
})();

/**
 * Get workspace-scoped cache key for session storage
 */
export function getSessionCacheKey(workspaceId: string): string {
  return `${ENV.sessionCacheKey}_${workspaceId}`;
}

/**
 * Utility to check if a feature is enabled
 */
export function isFeatureEnabled(feature: keyof Pick<EnvironmentConfig, 'enableLogging' | 'enableAnalytics' | 'enableErrorTracking'>): boolean {
  return ENV[feature];
}

// Log environment on initialization (development only)
// This will be completely removed in production builds by tree-shaking
if (ENV.enableLogging) {
  console.log('[BotForge] Environment initialized:', {
    environment: ENV.environment,
    useEmulators: ENV.useEmulators,
    apiBaseUrl: ENV.apiBaseUrl,
  });
}
